import type { Facet } from '../types/Facet';
import type { TaxonomyAttribute } from '../types/ProductsQueryState';

/**
 * pageCategories building blocks
 * ==============================
 *
 * `pageCategories` is the `>`-joined category breadcrumb sent to Vertex AI Search
 * for Commerce (as `page_categories`) and mirrored into the dataLayer
 * (`crs_categories`). It is assembled in different ways per PLP route, but always
 * in this one breadcrumb format:
 *
 *  - **CLP / PPLP** build it from the URL path via {@link urlPathToVertexPageCategory}.
 *  - **TPLP (`/shop`)** builds it from the active taxonomy facet selection via
 *    {@link levelsToPageCategories}, optionally appending the URL's filter values.
 *
 * The single place these are combined and read is `selectPageCategories`; this
 * module owns the shared format primitive and the taxonomy-levels builder.
 */

/** The delimiter Vertex uses between category-hierarchy levels (note the surrounding spaces). */
export const PAGE_CATEGORY_SEPARATOR = ' > ';

/** Within a single taxonomy level, multi-selected values are joined with this in the facet state. */
export const TAXONOMY_MULTISELECT_DELIMITER = ';';

/** The reserved URL/level placeholder for a skipped (unselected) taxonomy level. */
export const TAXONOMY_SKIP_SEGMENT = 'all';

/**
 * Joins breadcrumb segments with {@link PAGE_CATEGORY_SEPARATOR}, dropping empty
 * segments so a missing level never produces a blank ` >  > ` gap. The single
 * source of the breadcrumb format shared by every pageCategories builder.
 */
export const toBreadcrumb = (segments: string[]): string =>
  segments.filter(Boolean).join(PAGE_CATEGORY_SEPARATOR);

/**
 * The pageCategories breadcrumb for a CLP/PPLP, built straight from the URL path
 * — e.g. `/gifts/gifts-by-recipient/for-her` → `gifts > gifts-by-recipient > for-her`.
 *
 * These routes have no taxonomy facet hierarchy, so (unlike {@link levelsToPageCategories})
 * the path segments are the breadcrumb directly.
 */
export const urlPathToVertexPageCategory = (urlPath: string): string =>
  toBreadcrumb(urlPath.replace(/^\/|\/$/g, '').split('/'));

/**
 * The product-types-only pageCategories breadcrumb for a taxonomy (`/shop`) page,
 * derived from the **active taxonomy facet options** — e.g. `['jewellery > earrings']`.
 *
 * Unlike CLP/PPLP (which read straight off the URL path, see
 * {@link urlPathToVertexPageCategory}), the taxonomy route builds from the
 * resolved facet option *values* rather than the raw URL because it needs the
 * skipped-level (`all`) handling, multi-select rendering (`;`→`/`) and trailing
 * empty-level trimming that a flat path string can't express.
 *
 * The URL's filter values are appended on top of this baseline by
 * `selectPageCategories`, not here.
 */
export const levelsToPageCategories = (
  facets: Facet[],
  taxonomyAttributeAliases: TaxonomyAttribute[],
): string[] => {
  const facetByAlias = new Map(facets.map((facet) => [facet.alias, facet]));

  const taxonomyLevelValues = taxonomyAttributeAliases.map((alias) => {
    const facet = facetByAlias.get(alias);
    const activeOptions = facet?.options.filter((option) => option.active) ?? [];

    return activeOptions.length > 0
      ? activeOptions
          .map((option) => option.value)
          .sort()
          .join(TAXONOMY_MULTISELECT_DELIMITER)
      : TAXONOMY_SKIP_SEGMENT;
  });

  while (
    taxonomyLevelValues.length > 0 &&
    taxonomyLevelValues[taxonomyLevelValues.length - 1] === TAXONOMY_SKIP_SEGMENT
  ) {
    taxonomyLevelValues.pop();
  }

  return [
    toBreadcrumb(
      taxonomyLevelValues
        .filter((value) => value !== TAXONOMY_SKIP_SEGMENT)
        .map((value) => value.split(TAXONOMY_MULTISELECT_DELIMITER).join('/')),
    ),
  ];
};
